import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import programService from '../services/programService';
import { authService } from '../services/authService';
import formService from '../services/formService';
import ProgramVideoPlayer from '../components/ProgramVideoPlayer';
import '../styles/MyLibrary.css';

// Helper: flatten nested phases/days into a single list with indexes
const flattenDays = (phases = []) => {
  const list = [];
  phases.forEach((phase, pIdx) => {
    (phase.days || []).forEach((day, dIdx) => {
      list.push({ ...day, phaseIndex: pIdx, dayIndex: dIdx, globalDay: list.length + 1 });
    });
  });
  return list;
};

// Static component so React keeps it mounted between renders (prevents flicker)
function ProgramContent({
  content,
  selectedProgram,
  progress,
  unlockMessage,
  refreshContent,
  expandedPhase,
  togglePhase,
  expandedDay,
  toggleDay,
  openFormForDay,
  actionLoading,
  onOpenDownload,
  onCloseDownload,
  showDownloadModal,
  onDownloadImage,
}) {
  const isTruthyFlag = (value) => value === true || value === 1 || value === '1' || value === 'true';
  const phases = useMemo(() => (Array.isArray(content?.phases) ? content.phases : []), [content]);

  const trimmedPhases = useMemo(() => {
    const offers = Array.isArray(selectedProgram?.program_offers) ? selectedProgram.program_offers : [];
    let allowedDays = null;
    const selectedOfferId = selectedProgram?.access?.selected_offer_id || null;
    if (selectedProgram?.access?.selected_offer_id && offers.length) {
      const matched = offers.find(o => o.id === selectedProgram.access.selected_offer_id);
      if (matched && matched.access_days) {
        allowedDays = Number(matched.access_days);
      }
    }
    if (!allowedDays) return phases;
    let dayCounter = 0;
    return phases
      .map(phase => {
        const days = (phase.days || []).filter((day) => {
          // Hide offer-gated days unless the user purchased that exact offer.
          if (day?.require_offer_id && (!selectedOfferId || day.require_offer_id !== selectedOfferId)) {
            return false;
          }
          dayCounter += 1;
          return dayCounter <= allowedDays;
        });
        return { ...phase, days };
      })
      .filter(p => (p.days || []).length > 0);
  }, [phases, selectedProgram]);

  const flattenedDays = useMemo(() => flattenDays(trimmedPhases), [trimmedPhases]);
  const dayLookup = useMemo(() => {
    const map = {};
    flattenedDays.forEach((d) => {
      map[`${d.phaseIndex}-${d.dayIndex}`] = d;
    });
    return map;
  }, [flattenedDays]);
  const unlockedDay = progress?.unlocked_day || 1;

  const buildEmbedUrl = (url) => {
    if (!url) return '';
    try {
      if (url.includes('youtube.com/watch')) {
        const u = new URL(url);
        const videoId = u.searchParams.get('v');
        const start = u.searchParams.get('start') || u.searchParams.get('t');
        return `https://www.youtube.com/embed/${videoId}${start ? `?start=${start.replace('s', '')}` : ''}`;
      }
      if (url.includes('youtu.be/')) {
        const u = new URL(url);
        const videoId = u.pathname.replace('/', '');
        const start = u.searchParams.get('t') || u.searchParams.get('start');
        return `https://www.youtube.com/embed/${videoId}${start ? `?start=${start.replace('s', '')}` : ''}`;
      }
    } catch {
      // Fall through to raw url
    }
    return url;
  };

  const warmupPlayback = content?.warmup_playback;
  const warmupFallback = buildEmbedUrl(content?.warmup_video_url);
  const joinLink = selectedProgram?.access?.selected_offer_link || selectedProgram?.whatsapp_link || selectedProgram?.community_link || '';
  const showJoinButton = selectedProgram?.isPurchased && !!joinLink;

  if (!content) {
    return (
      <div className="purchased-detail-layout">
        <p style={{ padding: '24px' }}>Content will be available soon.</p>
      </div>
    );
  }

  return (
    <>
      <div className="purchased-detail-layout">
        <div className="program-title-section">
          <h1>{content.hero_title || `${selectedProgram?.duration || ''} Workouts`}</h1>
        </div>

        <div className="content-with-sidebar">
          <div className="left-sidebar">
            <div className="sidebar-image-box">
              <img
                src={selectedProgram?.image || '/assets/programs/images/program1.jpg'}
                alt="Program"
                className="sidebar-image-img"
              />
            </div>

            {selectedProgram?.isPurchased && selectedProgram?.download_image && (
              <div className="sidebar-download-box">
                <div
                  className="sidebar-download-image"
                  onClick={onOpenDownload}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onOpenDownload();
                  }}
                >
                  <img src={selectedProgram.download_image} alt="Downloadable content" />
                </div>
              </div>
            )}
          </div>

          <div className="right-content">
            <div className="content-section">
              <h2>{content.intro_title || selectedProgram?.name}</h2>
              <p>{content.intro_body || selectedProgram?.description || selectedProgram?.tagline}</p>
              {showJoinButton && (
                <button
                  className="btn-join-community"
                  onClick={() => {
                    if (joinLink) window.open(joinLink, '_blank', 'noopener');
                  }}
                  disabled={!joinLink}
                >
                  Join the group
                </button>
              )}
            </div>

            {(warmupPlayback?.url || warmupFallback || warmupPlayback?.type === 'processing' || warmupPlayback?.type === 'hls') && (
              <div className="content-section">
                <h2>{content.warmup_title || 'Warm Up:'}</h2>
                <div className="video-wrapper">
                  <ProgramVideoPlayer
                    playback={warmupPlayback}
                    fallbackUrl={warmupFallback}
                    title="Warm Up Video"
                    height={315}
                  />
                </div>
              </div>
            )}

            {progress && (
              <div className="progress-banner">
                <div>
                  Unlocked through day {progress.unlocked_day || 1} of {flattenedDays.length || 1}
                </div>
                {progress.next_unlock_on && (
                  <div className="progress-subtext">Next unlock available on {progress.next_unlock_on}</div>
                )}
                {unlockMessage && <div className="progress-subtext highlight">{unlockMessage}</div>}
              </div>
            )}

            <div className="phases-section">
              <h2>Let's Get Started:</h2>

              {trimmedPhases.length === 0 && (
                <div className="content-section">
                  <p>Daily workout links are not published yet for this program.</p>
                  <button className="btn-detail-buy" onClick={refreshContent}>Retry</button>
                </div>
              )}

              {trimmedPhases.map((phase, phaseIndex) => (
                <div key={phaseIndex} className={`phase-card ${expandedPhase === phaseIndex ? 'expanded' : ''}`}>
                  <button type="button" className="phase-header" onClick={(e) => { e.preventDefault(); togglePhase(phaseIndex); }}>
                    <div className="phase-info">
                      <span className="phase-title">{phase.title || `Phase ${phaseIndex + 1}`}</span>
                      {phase.goal && <span className="phase-goal">{phase.goal}</span>}
                    </div>
                    <svg className="phase-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>

                  {expandedPhase === phaseIndex && (
                    <div className="phase-content">
                      {(phase.days || []).map((item, dayIndex) => {
                        const key = `${phaseIndex}-${dayIndex}`;
                        const displayDay = item.day_number || item.day || dayIndex + 1;
                        const dayPlayback = item.playback;
                        const videoSrc = buildEmbedUrl(item.video_url);
                        const hasVideo =
                          dayPlayback?.url ||
                          videoSrc ||
                          dayPlayback?.type === 'processing' ||
                          dayPlayback?.type === 'hls';
                        const globalDay = dayLookup[key]?.globalDay || dayIndex + 1;
                        const isImmediate = isTruthyFlag(item.immediate_unlock);
                        const isEarly = isTruthyFlag(item.exception_unlock);
                        const offerId = selectedProgram?.access?.selected_offer_id;
                        const offerLocked = item.require_offer_id ? (offerId ? item.require_offer_id !== offerId : true) : false;
                        const locked = offerLocked
                          ? true
                          : isImmediate
                            ? false
                            : isEarly
                              ? globalDay > unlockedDay
                              : globalDay > unlockedDay;
                        const isCurrent = offerLocked ? false : (isImmediate ? true : globalDay === unlockedDay);

                        return (
                          <div key={key} className={`day-card ${expandedDay === key ? 'expanded' : ''} ${item.is_bonus ? 'bonus-card' : ''} ${locked ? 'locked' : 'unlocked'}`}>
                            <button type="button" className="day-header" onClick={(e) => { e.preventDefault(); toggleDay(key); }}>
                              <div className="day-info">
                                <span className="day-title">Day {displayDay}{item.is_bonus ? ' (Bonus)' : ''}:</span>
                                <span className="day-subtitle">{item.title}</span>
                              </div>
                              <div className="day-header-right">
                                <div className="day-tags">
                                  {isImmediate && <span className="tag tag-green">Immediate</span>}
                                  {isEarly && !isImmediate && <span className="tag tag-blue">Early</span>}
                                  {offerLocked && <span className="tag">Upgrade</span>}
                                  {locked ? <span className="tag">Locked</span> : <span className="tag tag-green">Unlocked</span>}
                                </div>
                                <svg className="day-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </div>
                            </button>
                            {expandedDay === key && (
                              <div className="day-content">
                                {locked ? (
                                  <div className="locked-note">
                                    Available after you click Next on the current day{progress?.next_unlock_on ? ` (next unlock: ${progress.next_unlock_on})` : ''}.
                                  </div>
                                ) : (
                                  <>
                                    {item.focus && <p><strong>Today's Focus:</strong> {item.focus}</p>}
                                    {item.action_step && <p><strong>Tiny Action Step:</strong> {item.action_step}</p>}
                                    {hasVideo && (
                                      <div className="video-wrapper">
                                        <ProgramVideoPlayer
                                          playback={dayPlayback}
                                          fallbackUrl={videoSrc}
                                          title={`Day ${displayDay}`}
                                          height={250}
                                        />
                                      </div>
                                    )}
                                    <div className="day-actions" style={{ gap: 12 }}>
                                      {item.join_community && (item.join_community_link || joinLink) && (
                                        <button
                                          className="btn-join-community"
                                          onClick={() => window.open(item.join_community_link || joinLink, '_blank', 'noopener')}
                                        >
                                          Join Community
                                        </button>
                                      )}
                                      {isCurrent ? (
                                        <button className="btn-next" onClick={() => openFormForDay(globalDay, item.form_id)} disabled={actionLoading}>
                                          {actionLoading ? 'Processing...' : 'Next'}
                                        </button>
                                      ) : (
                                        <div className="day-status-text">You already unlocked newer videos. You can rewatch this anytime.</div>
                                      )}
                                      {item.form_id && <div className="day-status-text">Form will appear after you click Next.</div>}
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showDownloadModal && selectedProgram?.download_image && (
        <div className="download-modal-overlay" onClick={onCloseDownload}>
          <div className="download-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={onCloseDownload} aria-label="Close">
              x
            </button>
            <div className="download-modal-image">
              <img src={selectedProgram.download_image} alt={`${selectedProgram?.name || 'Program'} download`} />
            </div>
            <button
              className="download-btn"
              onClick={() => onDownloadImage(selectedProgram.download_image)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download
            </button>
          </div>
        </div>
      )}
    </>
  );
}
export function MyPrograms({ user, onLogout }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [buyingProgramId, setBuyingProgramId] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programContent, setProgramContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');
  const [expandedPhase, setExpandedPhase] = useState(0);
  const [expandedDay, setExpandedDay] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(true);
  const [progress, setProgress] = useState(null);
  const [unlockMessage, setUnlockMessage] = useState('');
  const [formModal, setFormModal] = useState({ open: false, form: null, dayNumber: null });
  const [formValues, setFormValues] = useState({});
  const [showUpsell, setShowUpsell] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showWaitModal, setShowWaitModal] = useState(false);
  const [waitMessage, setWaitMessage] = useState('');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const navigate = useNavigate();

  const getUserEmail = (u) => {
    if (!u) return null;
    if (u.email) return u.email;
    if (u.user_metadata?.email) return u.user_metadata.email;
    return null;
  };

  const getDisplayNameFromUser = (u) => {
    if (!u) return 'Guest';
    const name = u.name || u.fullName || u.full_name || u.user_metadata?.full_name || u.user_metadata?.name;
    if (name && typeof name === 'string') return name;
    const email = getUserEmail(u);
    if (!email) return 'Guest';
    return email;
  };

  const getInitialsFromUser = (u) => {
    const email = getUserEmail(u);
    if (!email) return '??';
    return email.slice(0, 2).toUpperCase();
  };

  const [currentUser, setCurrentUser] = useState(user || null);
  useEffect(() => {
    const fetchUser = async () => {
      try {
        if (user && (user.email || user.user_metadata?.email)) {
          setCurrentUser(user);
          return;
        }
        const res = await authService.getCurrentUser();
        const u = res?.user || res?.data || res;
        if (u) setCurrentUser(u);
      } catch (err) {
        setCurrentUser(user);
      }
    };
    fetchUser();
  }, [user]);

  const displayName = getDisplayNameFromUser(currentUser);
  const userInitials = getInitialsFromUser(currentUser);
  const userEmail = getUserEmail(currentUser);

  useEffect(() => {
    fetchPrograms();
  }, []);

  // Update selectedProgram when programs list changes (e.g., after purchase)
  useEffect(() => {
    if (selectedProgram && programs.length > 0) {
      const updatedProgram = programs.find(p => p.program_id === selectedProgram.program_id);
      if (updatedProgram && updatedProgram.isPurchased !== selectedProgram.isPurchased) {
        setSelectedProgram(updatedProgram);
      }
    }
  }, [programs, selectedProgram]);

  const mergeProgramsWithHistory = (incoming, prevPrograms) => {
    const prevMap = new Map((prevPrograms || []).map(p => [p.program_id, p.wasCompleted || p.isCompleted]));
    return (incoming || []).map(p => ({
      ...p,
      wasCompleted: prevMap.get(p.program_id) || p.isCompleted || false,
    }));
  };

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const response = await programService.getMyPrograms();
      if (response.success) {
        const merged = mergeProgramsWithHistory(response.programs, programs);
        setPrograms(prev => mergeProgramsWithHistory(response.programs, prev));
        return merged;
      } else {
        const data = await programService.getAllPrograms();
        const merged = mergeProgramsWithHistory(data, programs);
        setPrograms(prev => mergeProgramsWithHistory(data, prev));
        return merged;
      }
    } catch (err) {
      try {
        const data = await programService.getAllPrograms();
        const merged = mergeProgramsWithHistory(data, programs);
        setPrograms(prev => mergeProgramsWithHistory(data, prev));
        return merged;
      } catch (fallbackErr) {
        console.error('Fallback error:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
    return null;
  };

  const fetchProgramContent = async (programId) => {
    if (!programId) return;
    setContentLoading(true);
    setContentError('');
    try {
      const data = await programService.getProgramContent(programId);
      setProgramContent(data);
      fetchProgress(programId);
      setExpandedPhase(0);
      setExpandedDay(null);
    } catch (err) {
      console.error('Error fetching program content:', err);
      const status = err?.response?.status;
      const message = status === 404
        ? 'Content not available yet for this program.'
        : status === 401
          ? 'Please log in again to view this program.'
          : 'Failed to load program content.';
      setContentError(message);
      setProgramContent(null);
    } finally {
      setContentLoading(false);
    }
  };

  const fetchProgress = async (programId) => {
    try {
      const data = await programService.getProgress(programId);
      if (data?.success !== false) {
        setProgress(data);
      }
    } catch (err) {
      console.error('Error fetching progress:', err);
    }
  };

  const handleUnlockNext = async () => {
    if (!selectedProgram?.program_id) return;
    setActionLoading(true);
    setUnlockMessage('');
    try {
      const res = await programService.unlockNextDay(selectedProgram.program_id);
      if (res.success) {
        fetchProgress(selectedProgram.program_id);
        setUnlockMessage(res.unlocked_day ? `Unlocked Day ${res.unlocked_day}` : 'Unlocked');
        // If completed or last unlock triggers upsell
        if (programContent && flattenDays(programContent.phases).length === res.unlocked_day) {
          setShowUpsell(true);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Only one video unlock per day';
      const nextDay = err?.response?.data?.next_unlock_on;
      
      // Show wait modal instead of inline message
      if (nextDay) {
        setWaitMessage(`You can only unlock one video per day. Please wait until ${nextDay} to unlock the next video.`);
      } else {
        setWaitMessage(msg);
      }
      setShowWaitModal(true);
    } finally {
      setActionLoading(false);
    }
  };

  const openFormForDay = async (dayNumber, formId) => {
    if (!formId) {
      handleUnlockNext();
      return;
    }
    try {
      const res = await formService.getForm(formId);
      if (res.success) {
        setFormValues({});
        setFormModal({ open: true, form: res.form, dayNumber });
      }
    } catch (err) {
      setUnlockMessage('Could not load form. Please try again.');
    }
  };

  const submitForm = async () => {
    if (!formModal.form) return;
    setActionLoading(true);
    try {
      await formService.submitResponse(formModal.form.id, {
        program_id: selectedProgram?.program_id,
        day_number: formModal.dayNumber,
        responses: formValues
      });
      setFormModal({ open: false, form: null, dayNumber: null });
      setFormValues({});
      await handleUnlockNext();
    } catch (err) {
      setUnlockMessage(err?.response?.data?.error || 'Failed to submit form');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      onLogout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleViewDetails = (program) => {
    // If program is completed and user clicks "Re-buy", trigger purchase flow
    if (program.isCompleted) {
      handleBuyNow(program);
      return;
    }
    
    setSelectedProgram(program);
    setContentError('');
    setExpandedPhase(0);
    setExpandedDay(null);
  };

  const handleBackToPrograms = () => {
    setSelectedProgram(null);
    setProgramContent(null);
    setProgress(null);
    setUnlockMessage('');
    setShowUpsell(false);
  };

  const handleUpsellDismiss = async () => {
    setShowUpsell(false);
    
    // Mark program as completed after upsell dismissal
    if (selectedProgram?.program_id) {
      try {
        await programService.completeProgram(selectedProgram.program_id);
        // Refresh programs to show updated completion status
        fetchPrograms();
      } catch (err) {
        console.error('Error completing program:', err);
      }
    }
  };

  const handleDownloadImage = (imageUrl) => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${selectedProgram?.name || 'program'}-download.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const togglePhase = (phaseIndex) => {
    setExpandedPhase(expandedPhase === phaseIndex ? null : phaseIndex);
    setExpandedDay(null);
  };

  const toggleDay = (dayIndex) => {
    setExpandedDay(expandedDay === dayIndex ? null : dayIndex);
  };

  const formatPriceINR = (val) => {
    const num = Number(val) || 0;
    return '\u20B9' + num.toLocaleString('en-IN');
  };

  const getPrimaryPrice = (program) => {
    const offers = Array.isArray(program?.program_offers) ? program.program_offers : [];
    if (offers.length > 0) {
      const prices = offers.map(o => Number(o.price_inr || 0)).filter(p => !isNaN(p));
      if (prices.length) return Math.min(...prices);
    }
    return Number(program?.priceINR ?? program?.price_inr ?? 0);
  };

  const getCheapestOffer = (program) => {
    const offers = Array.isArray(program?.program_offers) ? program.program_offers : [];
    if (!offers.length) return null;
    return offers.reduce((best, o) => {
      const price = Number(o.price_inr || 0);
      if (isNaN(price)) return best;
      if (!best) return o;
      const bestPrice = Number(best.price_inr || 0);
      return price < bestPrice ? o : best;
    }, null);
  };

  const sortedPrograms = [...programs].sort((a, b) => {
    return (b.isPurchased ? 1 : 0) - (a.isPurchased ? 1 : 0);
  });

  const filteredPrograms = activeTab === 'all' 
    ? sortedPrograms 
    : activeTab === 'my-programs'
      ? sortedPrograms.filter(p => p.isPurchased && !p.isCompleted)
    : activeTab === 'completed'
        ? sortedPrograms.filter(p => p.isCompleted || p.wasCompleted)
        : sortedPrograms.filter(p => p.category === activeTab);

  const handleBuyNow = async (program, offer = null) => {
    const hasValidSession = await authService.ensureUserSession();
    if (!hasValidSession) {
      localStorage.setItem('resume_checkout_program_id', program.program_id);
      sessionStorage.setItem('resume_checkout_program_id', program.program_id);
      if (offer?.id) {
        localStorage.setItem('resume_checkout_offer_id', offer.id);
        sessionStorage.setItem('resume_checkout_offer_id', offer.id);
      }
      window.dispatchEvent(new CustomEvent('OPEN_AUTH_MODAL', { detail: { tab: 'login' } }));
      return;
    }
    setBuyingProgramId(program.program_id);
      const selectedOffer = offer || getCheapestOffer(program);
    try {
      const orderData = await programService.createOrder(program.program_id, selectedOffer?.id || null);
      const orderId = orderData.order_id;
      const amountPaise = orderData.amount_paise || orderData.amount * 100 || 0;
      const keyId = orderData.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID || '';
      if (!keyId) {
        setModalMessage('Razorpay key not configured. Please contact support.');
        setIsSuccess(false);
        setShowErrorModal(true);
        setBuyingProgramId(null);
        return;
      }
      if (typeof window.Razorpay === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
          document.body.appendChild(script);
        });
      }
      const options = {
        key: keyId,
        amount: amountPaise,
        currency: 'INR',
        name: 'Get Fit with Daniella',
        description: program.name,
        order_id: orderId,
        handler: async (response) => {
          try {
          const verify = await programService.verifyPayment(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature,
            program.program_id,
            selectedOffer?.id || null
          );
            if (verify?.success) {
              setModalMessage('Payment successful! Access granted.');
              setIsSuccess(true);
              setShowSuccessModal(true);
              const latestPrograms = await fetchPrograms();
              if (latestPrograms && selectedProgram?.program_id) {
                const updated = latestPrograms.find(p => p.program_id === selectedProgram.program_id);
                if (updated) {
                  setSelectedProgram(updated);
                } else {
                  setSelectedProgram(prev => prev ? { ...prev, isPurchased: true } : prev);
                }
              } else {
                setSelectedProgram(prev => prev ? { ...prev, isPurchased: true } : prev);
              }
              setActiveTab('my-programs');
              setTimeout(() => setShowSuccessModal(false), 2000);
            } else {
              setModalMessage('Payment verification failed. Please try again.');
              setIsSuccess(false);
              setShowErrorModal(true);
            }
          } catch (e) {
            setModalMessage('Payment verification error.');
            setIsSuccess(false);
            setShowErrorModal(true);
          }
        },
        prefill: { email: user?.email || '' },
        theme: { color: '#3b82f6' },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setModalMessage('Payment cancelled or failed. Please try again.');
        setIsSuccess(false);
        setShowErrorModal(true);
      });
      rzp.open();
    } catch (err) {
      console.error('Create order error:', err);
      // Handle duplicate purchase error
      if (err?.response?.status === 400 && err?.response?.data?.message?.includes('already have active access')) {
        setModalMessage('You already have access to this program. Refreshing your programs...');
        setIsSuccess(true);
        setShowSuccessModal(true);
        fetchPrograms(); // Refresh to show updated purchase status
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        const errorMessage = err?.response?.data?.message || 'Unable to start payment flow.';
        setModalMessage(errorMessage);
        setIsSuccess(false);
        setShowErrorModal(true);
      }
    } finally {
      setBuyingProgramId(null);
    }
  };

  useEffect(() => {
    if (selectedProgram?.program_id && selectedProgram.isPurchased) {
      fetchProgramContent(selectedProgram.program_id);
    } else {
      setProgramContent(null);
      setContentError('');
      setProgress(null);
    }
  }, [selectedProgram]);

  const refreshContent = () => {
    if (selectedProgram?.program_id) {
      fetchProgramContent(selectedProgram.program_id);
    }
  };

  return (
    <div className={`library-container ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      <aside className="library-sidebar">
        <div className="library-logo">
          <img src="/assets/logo-img.jpg" alt="Get Fit with Daniella" />
          <span>Get Fit with Daniella</span>
        </div>
        <div className="library-mobile-user">
          <div className="user-avatar">{userInitials}</div>
          <span className="user-email">{userEmail || displayName}</span>
        </div>
        <nav className="library-nav">
          <ul>
            <li className="nav-item active">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <span>Programs</span>
            </li>
          </ul>
        </nav>
        <div className="library-sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="library-main">
        <div className="library-header">
          <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="mobile-toggle" aria-label="Open navigation" onClick={() => setMobileMenuOpen(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <div>
              <h1>Programs</h1>
              <p>{programs.length} course{programs.length !== 1 ? 's' : ''} available</p>
            </div>
          </div>
          <div className="library-user">
            <div className="user-avatar">{userInitials}</div>
            <span className="user-email">{userEmail || displayName}</span>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
        )}

        {(showSuccessModal || showErrorModal) && (
          <div className="modal-overlay" onClick={() => { setShowSuccessModal(false); setShowErrorModal(false); }}>
            <div className="success-modal" onClick={(e) => e.stopPropagation()}>
              <div className={`success-icon ${!isSuccess ? 'error' : ''}`}>
                {isSuccess ? (
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="9 12 12 15 16 10"/>
                  </svg>
                ) : (
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                )}
              </div>
              <h3>{isSuccess ? 'Success!' : 'Oops!'}</h3>
              <p>{modalMessage}</p>
              <button className={`btn-modal-close ${!isSuccess ? 'error' : ''}`} onClick={() => { setShowSuccessModal(false); setShowErrorModal(false); if (isSuccess) window.location.reload(); }}>
                {isSuccess ? 'Continue' : 'Try Again'}
              </button>
            </div>
          </div>
        )}

        {showUpsell && programContent && (
          <div className="modal-overlay" onClick={() => setShowUpsell(false)}>
            <div className="upsell-modal" onClick={(e) => e.stopPropagation()}>
              {programContent.upsell_image && (
                <img src={programContent.upsell_image} alt="Next program" className="upsell-image" />
              )}
              <h3>{programContent.upsell_title || 'Keep going!'}</h3>
              <p>{programContent.upsell_text || 'Continue your momentum with the next program.'}</p>
              <div className="upsell-actions">
                {programContent.upsell_program_id && (
                  <button className="btn-primary" onClick={() => navigate(`/programs/${programContent.upsell_program_id}`)}>
                    View Program
                  </button>
                )}
                {(programContent.upsell_secondary_enabled === true || programContent.upsell_secondary_enabled === 1 || programContent.upsell_secondary_enabled === '1') && (
                  <button
                    className="btn-secondary btn-book-call"
                    style={{
                      backgroundColor: programContent.upsell_secondary_bg_color || '#0f766e',
                      color: programContent.upsell_secondary_text_color || '#ffffff',
                      borderColor: programContent.upsell_secondary_bg_color || '#0f766e'
                    }}
                    onClick={() => {
                      if (programContent.upsell_secondary_link) {
                        window.open(programContent.upsell_secondary_link, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    disabled={!programContent.upsell_secondary_link}
                  >
                    {programContent.upsell_secondary_text || 'Book a Call'}
                  </button>
                )}
                <button className="btn-secondary" onClick={handleUpsellDismiss}>Maybe later</button>
              </div>
            </div>
          </div>
        )}

        {formModal.open && formModal.form && (
          <div className="modal-overlay" onClick={() => setFormModal({ open: false, form: null, dayNumber: null })}>
            <div className="form-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{formModal.form.title}</h3>
              {formModal.form.intro && <p className="form-intro">{formModal.form.intro}</p>}
              <div className="form-fields">
                {(formModal.form.fields || []).map((field, idx) => {
                  const val = formValues[field.label] || '';
                  return (
                    <div key={idx} className="form-field">
                      <label>{field.label}{field.required ? ' *' : ''}</label>
                      {field.type === 'textarea' ? (
                        <textarea
                          rows="3"
                          value={val}
                          placeholder={field.placeholder}
                          onChange={(e) => setFormValues({ ...formValues, [field.label]: e.target.value })}
                        />
                      ) : (
                        <input
                          type={field.type === 'number' ? 'number' : 'text'}
                          value={val}
                          placeholder={field.placeholder}
                          onChange={(e) => setFormValues({ ...formValues, [field.label]: e.target.value })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="form-actions">
                <button className="btn-secondary" onClick={() => setFormModal({ open: false, form: null, dayNumber: null })}>Cancel</button>
                <button className="btn-primary" onClick={submitForm} disabled={actionLoading}>
                  {actionLoading ? 'Saving...' : 'Submit & Next'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showWaitModal && (
          <div className="modal-overlay" onClick={() => setShowWaitModal(false)}>
            <div className="success-modal" onClick={(e) => e.stopPropagation()}>
              <div className="success-icon" style={{ background: '#f59e0b' }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3>Please Wait</h3>
              <p>{waitMessage}</p>
              <button className="btn-modal-close" onClick={() => setShowWaitModal(false)}>
                Understood
              </button>
            </div>
          </div>
        )}

        {!selectedProgram && (
          <div className="library-tabs">
            <button className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>All Courses</button>
            <button className={`tab-btn ${activeTab === 'my-programs' ? 'active' : ''}`} onClick={() => setActiveTab('my-programs')}>My Programs</button>
            <button className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`} onClick={() => setActiveTab('completed')}>Completed</button>
            <button className="tab-btn refresh-btn" onClick={fetchPrograms} disabled={loading} title="Refresh">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: loading ? 'rotate(360deg)' : 'none', transition: 'transform 1s' }}>
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
          </div>
        )}

        {loading ? (
          <div className="library-loading">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        ) : selectedProgram ? (
          <div className="library-program-detail">
            <button className="btn-back-to-programs" onClick={handleBackToPrograms}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Back to Programs
            </button>

            {selectedProgram.isPurchased ? (
              contentLoading ? (
                <div className="library-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading your program content...</p>
                </div>
              ) : contentError ? (
                <div className="library-empty">
                  <p>{contentError}</p>
                  <button className="btn-detail-buy" onClick={refreshContent}>Retry</button>
                </div>
              ) : (
                <ProgramContent
                  content={programContent}
                  selectedProgram={selectedProgram}
                  progress={progress}
                  unlockMessage={unlockMessage}
                  refreshContent={refreshContent}
                  expandedPhase={expandedPhase}
                  togglePhase={togglePhase}
                  expandedDay={expandedDay}
                  toggleDay={toggleDay}
                  openFormForDay={openFormForDay}
                  actionLoading={actionLoading}
                  onOpenDownload={() => setShowDownloadModal(true)}
                  onCloseDownload={() => setShowDownloadModal(false)}
                  showDownloadModal={showDownloadModal}
                  onDownloadImage={handleDownloadImage}
                />
              )
            ) : (
              <div className="detail-layout">
                <div className="detail-image-section">
                  <div className="detail-image-wrapper">
                    <img src={selectedProgram.image || '/placeholder-program.jpg'} alt={selectedProgram.name} className="detail-main-image" />
                  </div>
                </div>
                <div className="detail-content-section">
                  <h1 className="detail-title">{selectedProgram.duration} Program: {selectedProgram.name}</h1>
                  <div className="detail-welcome">
                    <h2>Welcome:</h2>
                    <p>{selectedProgram.description || selectedProgram.tagline || 'Join this transformative program.'}</p>
                  </div>
                  <div className="detail-includes">
                    <h2>What you get:</h2>
                    <ul className="includes-list">
                      <li>Personalized workout plans</li>
                      <li>Nutrition guidance</li>
                      <li>Expert coaching</li>
                      <li>Community access</li>
                    </ul>
                  </div>
                  {Array.isArray(selectedProgram.program_offers) && selectedProgram.program_offers.length > 0 ? (
                    <div className="offer-stack">
                      <h3 className="offers-title">Choose your plan</h3>
                      {selectedProgram.program_offers.map((offer) => (
                        <div key={offer.id} className="offer-card">
                          <div className="offer-left">
                            <div className="offer-label">{offer.label || 'Plan'}</div>
                            {(offer.weeks || offer.access_days) && (
                              <div className="offer-duration">{offer.weeks ? `${offer.weeks} weeks` : `${offer.access_days} days`}</div>
                            )}
                            {offer.description && <div className="offer-desc">{offer.description}</div>}
                          </div>
                          <div className="offer-right">
                            <div className="offer-price">{formatPriceINR(offer.price_inr)}</div>
                            <button
                              className="btn-detail-buy"
                              onClick={() => handleBuyNow(selectedProgram, offer)}
                              disabled={buyingProgramId === selectedProgram.program_id}
                            >
                              {buyingProgramId === selectedProgram.program_id ? 'Processing...' : (offer.cta_text || 'Buy Now')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="detail-cta-section">
                      <div className="detail-cta-price">
                        <span className="price-label">Price:</span>
                        <span className="price-value">{formatPriceINR(selectedProgram.price_inr)}</span>
                      </div>
                      <div className="detail-cta-buttons">
                        <button className="btn-detail-buy" onClick={() => handleBuyNow(selectedProgram)} disabled={buyingProgramId === selectedProgram.program_id}>
                          {buyingProgramId === selectedProgram.program_id ? 'Processing...' : 'Buy Now'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : filteredPrograms.length === 0 ? (
          <div className="library-empty">
            <p>{activeTab === 'completed' ? 'No programs completed yet' : activeTab === 'my-programs' ? 'No purchased programs yet' : 'No courses available'}</p>
          </div>
        ) : (
          <div className="library-grid">
            {filteredPrograms.map((program) => (
              <div key={program.program_id} className={`library-card ${program.isPurchased ? (program.isCompleted ? 'completed' : 'purchased') : 'locked'}`}>
                <div className="card-image" style={{ background: program.color || '#6a3df0' }}>
                  {program.image && <img src={program.image} alt={program.name} />}
                  {!program.isPurchased && (
                    <div className="lock-overlay">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                    </div>
                  )}
                  <div className="card-badge">
                    {program.isCompleted || program.wasCompleted
                      ? `Completed${program.purchaseCount > 1 ? ` (${program.purchaseCount})` : ''}`
                      : (program.isPurchased ? 'Purchased' : (program.badge || 'Locked'))}
                  </div>
                </div>
                <div className="card-content">
                  <h3>{program.name}</h3>
                  <p className="card-tagline">{program.tagline}</p>
                  <div className="card-meta">
                    <span>{program.duration}</span>
                    <span className="separator">•</span>
                    <span>{program.timeCommitment}</span>
                  </div>
                  <div className="card-footer">
                    {!program.isPurchased && (
                      <div className="card-price">
                        <span className="currency">₹</span>
                        <span className="amount">{getPrimaryPrice(program).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                    <div className="card-buttons">
                      {program.isPurchased ? (
                        <button className={`btn-continue-program ${program.isCompleted ? 'completed' : ''}`} onClick={() => handleViewDetails(program)}>
                          {program.isCompleted ? 'Re-buy' : 'Continue'}
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </button>
                      ) : (
                        <>
                          <button className="btn-buy-now" onClick={() => handleBuyNow(program)} disabled={buyingProgramId === program.program_id}>
                            {buyingProgramId === program.program_id ? 'Processing...' : 'Buy Now'}
                          </button>
                          <button className="btn-unlock" onClick={() => handleViewDetails(program)}>
                            View Details
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default MyPrograms;

